import { ReactNode } from 'react';

import { GeoArea } from '@/types/GeoArea';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { slugToTitleCase } from '@/utils/strings';

import { AuctionsNearMeRouteParams } from '../types/AuctionsNearMe.types';

type GetPageTitleParams = {
    /** From `useAppFormatMessage`. Passed in rather than called here so this stays a plain
     *  function with a plain spec, instead of becoming a hook for one string. */
    formatMessage: ReturnType<typeof useAppFormatMessage>;
    geoArea: GeoArea | undefined;
    geocoderFailed: boolean;
    hasFilteredResults: boolean;
    /**
     * The results came from towns around this location because it had none of its own, so the
     * heading has to say "near" rather than "in" — the auctions are genuinely somewhere else.
     */
    hasBorrowedResults: boolean;
    routeParams: AuctionsNearMeRouteParams;
    searchTerm: string;
};

// Derive location name from URL params for SSR when geoArea is not found
function getLocationFromRouteParams(routeParams: AuctionsNearMeRouteParams): string | undefined {
    if ('city' in routeParams && routeParams.city) {
        const decodedCity = decodeURIComponent(routeParams.city);
        const formattedCity = slugToTitleCase(decodedCity);

        if ('state' in routeParams && routeParams.state) {
            const formattedState = decodeURIComponent(routeParams.state).toUpperCase();
            return `${formattedCity}, ${formattedState}`;
        }
        return formattedCity;
    }

    if ('state' in routeParams && routeParams.state) {
        return slugToTitleCase(decodeURIComponent(routeParams.state));
    }

    if ('country' in routeParams && routeParams.country) {
        return slugToTitleCase(decodeURIComponent(routeParams.country));
    }

    return undefined;
}

export function getPageTitle({
    formatMessage,
    geoArea,
    geocoderFailed,
    hasFilteredResults,
    hasBorrowedResults,
    routeParams,
    searchTerm,
}: GetPageTitleParams): ReactNode {
    const locationTitleKey = hasBorrowedResults
        ? (['auctionsNearMe', 'nearbyPageTitle'] as const)
        : (['auctionsNearMe', 'customSearchPageTitle'] as const);

    const noAuctionsFound = (locationSearched: string): ReactNode =>
        formatMessage(['auctionsNearMe', 'noAuctionsFound'], { values: { locationSearched } });

    // User searches for a location that does not match any geo area (geocoder failed) - e.g. "Anytown"
    if (geocoderFailed && searchTerm) {
        return noAuctionsFound(searchTerm);
    }

    // User searches for a location via geocoder and returns NO results - e.g. "Akron"
    if (searchTerm && !hasFilteredResults) {
        return noAuctionsFound(searchTerm);
    }

    // User searches for a location via geocoder and HAS results - e.g. "Cleveland"
    if (searchTerm && hasFilteredResults) {
        return formatMessage(['auctionsNearMe', 'customSearchPageTitle'], {
            values: { locationSearched: searchTerm },
        });
    }

    // User navigates to a geo area page
    if (geoArea) {
        // No results for this geo area
        if (!hasFilteredResults) {
            return noAuctionsFound(geoArea.geoAreaName);
        }

        // Has results - use pageTitle if available, otherwise use geoAreaName
        if (geoArea.pageTitle) {
            return geoArea.pageTitle;
        }

        // Fallback to geoAreaName-based title for SSR when pageTitle might not be set
        return formatMessage(locationTitleKey, { values: { locationSearched: geoArea.geoAreaName } });
    }

    // SSR fallback: derive location from URL params when no geoArea is found
    // This handles URLs like /pa/pineville where pineville isn't a predefined geoArea
    const locationFromUrl = getLocationFromRouteParams(routeParams);
    if (locationFromUrl) {
        if (!hasFilteredResults) {
            return noAuctionsFound(locationFromUrl);
        }
        return formatMessage(locationTitleKey, { values: { locationSearched: locationFromUrl } });
    }

    // Default page title (base page with no geoArea)
    return formatMessage(['auctionsNearMe', 'defaultPageTitle']);
}
